import React from 'react';
import { Box, Typography, Grid, Paper, Stack } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';

const HighRiskFactorsGuide = () => {
  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
        High-Risk Factors for Falls
      </Typography>
      
      <Grid container spacing={4}>
        {/* Left Column - High-Risk Conditions */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: 'error.light'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Understanding High-Risk Conditions
            </Typography>
            
            <Typography variant="body2" paragraph sx={{ mb: 3, color: 'text.secondary' }}>
              Certain medical conditions significantly increase fall risk due to their effects on balance, mobility, and cognition. 
              Understanding the mechanisms behind this increased risk is essential for caregivers.
            </Typography>
            
            <Stack spacing={3}>
              {/* Neurological Conditions */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningIcon sx={{ color: 'error.light', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Neurological Conditions
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Conditions affecting the nervous system often impact balance, coordination, and muscle control
                  </Typography>
                </Box>
              </Box>
              
              {/* Musculoskeletal Conditions */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningIcon sx={{ color: 'error.light', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Musculoskeletal Conditions
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Affect strength, flexibility, and joint function, altering gait patterns
                  </Typography>
                </Box>
              </Box>
              
              {/* Cognitive Conditions */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningIcon sx={{ color: 'error.light', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Cognitive Conditions
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Impact awareness, judgment, and the ability to recognize and respond to hazards
                  </Typography>
                </Box>
              </Box>
              
              {/* Sensory Impairments */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningIcon sx={{ color: 'error.light', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Sensory Impairments
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Reduce environmental awareness and the ability to detect obstacles or changes in terrain
                  </Typography>
                </Box>
              </Box>
              
              {/* Stroke */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningIcon sx={{ color: 'error.light', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Stroke
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    May result in one-sided weakness, balance issues, and visual field deficits
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        
        {/* Right Column - High-Risk Demographics */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              High-Risk Demographics
            </Typography>
            
            <Stack spacing={3}>
              {/* Adults over 65 */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <PersonIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Adults over 65
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Risk increases with age due to multiple factors including decreased muscle mass and sensory changes
                  </Typography>
                </Box>
              </Box>
              
              {/* Previous Fall History */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <PersonIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Previous Fall History
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Those who have fallen before are at significantly higher risk of falling again
                  </Typography>
                </Box>
              </Box>
              
              {/* Polypharmacy Patients */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <PersonIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Polypharmacy Patients
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Taking multiple medications increases risk due to side effects and interactions
                  </Typography>
                </Box>
              </Box>
              
              {/* Recent Hospital Discharge */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <PersonIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Recent Hospital Discharge
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Deconditioning, medication changes, and new environments increase risk
                  </Typography>
                </Box>
              </Box>
              
              {/* Individuals with Assistive Devices */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <PersonIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Individuals with Assistive Devices
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    May indicate mobility issues and require proper training for safe use
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HighRiskFactorsGuide;
